import {defineStore} from 'pinia'
import {CommonNs, type UIPreferencesNs, type UserNs, WorkspaceNs} from "@shared/types";
import {AccountApi, UserApi} from "@/api";
import {useWorkspaceStore} from "@shared/stores/workspace.store";
import {useTheme} from "@shared/composables/use-theme";


export const useUserStore = defineStore("user", {
    state: () => ({
        userInfo: {} as UserNs.UserInfo,
        uiPreferences: {} as UIPreferencesNs.UIPreferences,
    }),
    getters: {
        getUserInfo: (state): UserNs.UserInfo => state.userInfo,

        getUserAvatar: (state): UserNs.UserAvatar => ((state.userInfo.avatar || state.userInfo.socialAvatar) ? {
            avatar: state.userInfo.avatar,
            socialAvatar: state.userInfo.socialAvatar,
            fullNameAvatar: state.userInfo.fullNameAvatar,
            isUrl: true,
        } : {
            avatar: '',
            socialAvatar: '',
            fullNameAvatar: state.userInfo.fullNameAvatar,
            isUrl: false,
        }),

        getUserProfile: (state): UserNs.UserProfile => {
            const wsStore = useWorkspaceStore()
            const selectedWs = wsStore.getSelectedWorkspace;
            const profile = {
                fullName: state.userInfo.fullName,
                avatar: {
                    avatar: state.userInfo.avatar,
                    socialAvatar: state.userInfo.socialAvatar,
                    fullNameAvatar: state.userInfo.fullNameAvatar,
                    isUrl: true,
                },
                workspaceRole: WorkspaceNs.WorkspaceRoleEnum[selectedWs.userRole as keyof typeof WorkspaceNs.WorkspaceRoleEnum],
            } as UserNs.UserProfile;
            return profile;
        },

        getUserName: (state): string => state.userInfo.userName,

        getSelectedTheme: (state): 'DARK' | 'LIGHT' => state.uiPreferences.selectedTheme,

        getDashboardViewMode: (state): 'GRID' | 'LIST' => state.uiPreferences.dashboard?.viewMode || 'GRID',

        isDarkModeOn: (state): boolean => state.uiPreferences.selectedTheme === 'DARK',

        getUiPreferences: (state): UIPreferencesNs.UIPreferences => state.uiPreferences,

    },
    actions: {
        async getUserMe(): Promise<UserNs.UserInfo> {
            return UserApi.userMe()
                .then((payload: UserNs.UserInfo) => {
                    this.userInfo = payload;
                    return this.userInfo;
                })
        },

        async loadUiPreferences(): Promise<UIPreferencesNs.UIPreferences> {
            return AccountApi.getUiPreferences()
                .then((preferences) => {
                    let uiPreferences = preferences as any;
                    if (!uiPreferences) {
                        uiPreferences = {selectedTheme: 'LIGHT'};
                    }
                    if (!uiPreferences.featureBoard) {
                        uiPreferences.featureBoard = {
                            focusModeOn: false,
                            showBlocks: true,
                            showProperties: true,
                            isEditorExpanded: true,
                            showSitemap: true,
                            showSitemapContent: true,
                        };
                    }
                    if (!uiPreferences.screenBoard) {
                        uiPreferences.screenBoard = {
                            focusModeOn: false,
                            showBlocks: true,
                            showProperties: true,
                            isEditorExpanded: true,
                            showSitemap: true,
                            showSitemapContent: true,
                        };
                    }
                    if (!uiPreferences.dashboard) {
                        uiPreferences.dashboard = {
                            viewMode: 'GRID'
                        };
                    }

                    this.uiPreferences = uiPreferences;

                    useTheme().hydrateFromServer(this.uiPreferences.selectedTheme);
                    return this.uiPreferences;
                })
        },

        async setDashboardPreferences(payload: {
            preferences: string,
            value: boolean | string
        }): Promise<UIPreferencesNs.UIPreferences> {
            const pref = this.uiPreferences.dashboard as any;
            pref[payload.preferences] = payload.value;

            await AccountApi.updateUiPreferences(this.uiPreferences);
            return this.uiPreferences;
        },

        async setSelectedTheme(
            payload: UIPreferencesNs.UIPreferences,
        ): Promise<UserNs.Theme> {
            return UserApi.setSelectedTheme(payload)
                .then((response: UserNs.Theme) => {
                    this.uiPreferences.selectedTheme = response.selectedTheme || 'LIGHT';

                    useTheme().setMode(this.uiPreferences.selectedTheme);
                    return response;
                })
        },

        setBoardPreferences(
            payload: { board: 'FEATURE' | 'SCREEN', preferences: string, value: boolean },
        ): UIPreferencesNs.UIPreferences {

            let pref;
            if (payload.board === 'FEATURE') {
                pref = this.uiPreferences.featureBoard as any;
            } else {
                pref = this.uiPreferences.screenBoard as any;
            }

            pref[payload.preferences] = payload.value;
            if (payload.preferences === 'focusModeOn') {
                pref.showSitemap = !payload.value;
                if (payload.board === 'FEATURE') {
                    pref.showProperties = !payload.value;
                }
            }
            if (payload.preferences === 'showSitemap' || payload.preferences === 'showProperties') {
                if (payload.board === 'FEATURE') {
                    pref.focusModeOn = !pref.showSitemap && !pref.showProperties;
                } else {
                    pref.focusModeOn = !pref.showSitemap;
                }
            }

            AccountApi.updateUiPreferences(this.uiPreferences).then(() => {
            });

            return this.uiPreferences;
        },

        async setOnboarding(): Promise<CommonNs.AnyResponse> {
            return UserApi.setOnboarding().then((response) => {

                return response;
            });
        },
    }
});
